import { createContext, useContext, useEffect, useMemo, useState, useRef, Suspense } from 'react'
// lazyWithReload wraps React.lazy so a stale browser bundle (post-deploy)
// doesn't blank the page when a code-split chunk 404s. See
// src/utils/lazyWithReload.js for the heuristic and reload-once guard.
import { lazyWithReload as lazy, clearChunkReloadFlag } from './utils/lazyWithReload.js'
import { Routes, Route, Navigate, useNavigate, useLocation, useSearchParams, useNavigationType } from 'react-router-dom'

// Scrolls to top on forward navigation; preserves browser-restored
// scroll on back/forward (POP).
//
// Pre-fix this always scrolled to (0,0), which overrode the browser's
// natural scroll restoration on back navigation. Result: navigating
// from WineDetail → EditWine → back returned to the TOP of WineDetail
// instead of the scroll position the user left. (2026-05-14 mobile
// testing pass.)
//
// Modern browsers default `history.scrollRestoration` to 'auto' and
// remember the scroll position per history entry. By skipping the
// forced scroll-to-top on POP we let that mechanism do its job.
function ScrollToTop() {
  const { pathname } = useLocation()
  const navigationType = useNavigationType()
  useEffect(() => {
    if (navigationType !== 'POP') {
      window.scrollTo(0, 0)
    }
  }, [pathname, navigationType])
  return null
}
import vinaraiIcon from './assets/icon.svg'
// Statically imported because RequireAuth needs it synchronously to
// gate consumer routes on web (no Suspense flash). Component is ~3KB.
import WebMobileOnlyNotice from './components/WebMobileOnlyNotice.jsx'
// VIN-2026-59 Phase 2 (2026-05-28): the contextual AI chat overlay +
// its open helper. Mounted once globally; any surface (FAB, WOTD,
// WineDetail, cellar card) calls openWineChat(wine) to pop it.
import WineChatOverlay from './components/WineChatOverlay.jsx'
import { openWineChat } from './utils/aiChat.js'
import { onAuthStateChanged, signOut, updateProfile } from 'firebase/auth'
import { auth, db } from './firebase.js'
import { doc, getDoc, getDocFromServer, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore'
// VIN-2026-36 (2026-05-26): claimEventInvitesForUser, attributeShopSignup,
// loadStaffStatus, and resolveMerchantContextFromEntitlement all run AFTER
// auth completes — none of them are needed on the cold-start render path.
// Moved from static imports to dynamic import() inside their respective
// call sites so they don't bloat the entry chunk. Aggregate impact: ~850
// lines of source (eventInvites.js 228, shopAttribution.js 135,
// staffStatus.js 225, merchantEntitlements.js 262) deferred from the
// initial 145KB-gzipped index chunk. Each one round-trips once on first
// fire after sign-in (essentially free on native — chunks are local —
// and a one-time CDN fetch on web that warms the cache for subsequent
// sign-ins).
import { useBadgeCounts } from './hooks/useBadgeCounts.js'
// VIN-2025-08 (2026-05-26): bound getIdToken() with a 10s ceiling so a
// hung token refresh can't lock up the entire app boot.
import { withTimeout } from './utils/withTimeout.js'
import { ensureCellarForProfile, clearCellarCache } from './utils/cellars.js'
import { ThemeProvider } from './context/ThemeProvider.jsx'
import { installDiagnostics } from './utils/diagnostics.js'

// Mount the bug-report diagnostics service at module load — earlier than
// the first React render so route-change history + console + network
// recording all catch the very first navigation. installDiagnostics is
// idempotent so HMR doesn't double-wrap.
installDiagnostics()

// Route-level code splitting. Every page becomes its own chunk so the
// initial bundle stays small — first paint on mobile was suffering from
// a ~1.7MB single-chunk app. A Suspense fallback matching the app's
// loading spinner handles the in-between state while a chunk loads.
const Home            = lazy(() => import('./pages/Home.jsx'))
const AddWine         = lazy(() => import('./pages/AddWine.jsx'))
const CheckWine       = lazy(() => import('./pages/CheckWine.jsx'))
// VIN-2026-63 Ship D: the unified camera-first scan (center [Scan] tab).
const ScanFlow        = lazy(() => import('./pages/ScanFlow.jsx'))
const EditWine        = lazy(() => import('./pages/EditWine.jsx'))
const WineDetail      = lazy(() => import('./pages/WineDetail.jsx'))
const TastingFlow     = lazy(() => import('./pages/TastingFlow.jsx'))
const TastingHistory  = lazy(() => import('./pages/TastingHistory.jsx'))
const VinaraiPage     = lazy(() => import('./pages/Sommelier.jsx'))
const Login           = lazy(() => import('./pages/Login.jsx'))
const SignInFinish    = lazy(() => import('./pages/SignInFinish.jsx'))
const BulkUpload      = lazy(() => import('./pages/BulkUpload.jsx'))
const Onboarding      = lazy(() => import('./pages/Onboarding.jsx'))
const CreateEvent     = lazy(() => import('./pages/CreateEvent.jsx'))
const EventLobby      = lazy(() => import('./pages/EventLobby.jsx'))
const JoinEvent       = lazy(() => import('./pages/JoinEvent.jsx'))
const LiveTasting     = lazy(() => import('./pages/LiveTasting.jsx'))
const EventSummary    = lazy(() => import('./pages/EventSummary.jsx'))
const PublicEvents    = lazy(() => import('./pages/PublicEvents.jsx'))
const EventsHosting   = lazy(() => import('./pages/EventsHosting.jsx'))
const EventsAttending = lazy(() => import('./pages/EventsAttending.jsx'))
const Attended        = lazy(() => import('./pages/Attended.jsx'))
const Tickets         = lazy(() => import('./pages/Tickets.jsx'))
const TicketDetail    = lazy(() => import('./pages/TicketDetail.jsx'))
const TicketClaim     = lazy(() => import('./pages/TicketClaim.jsx'))
const Preferences     = lazy(() => import('./pages/Preferences.jsx'))
const SharedWineView  = lazy(() => import('./pages/SharedWineView.jsx'))
const JoinTable       = lazy(() => import('./pages/JoinTable.jsx'))
const JoinCellar      = lazy(() => import('./pages/JoinCellar.jsx'))
const BetaInvite      = lazy(() => import('./pages/BetaInvite.jsx'))
const Admin           = lazy(() => import('./pages/Admin.jsx'))
const Sharing         = lazy(() => import('./pages/Sharing.jsx'))
const Invitations     = lazy(() => import('./pages/Invitations.jsx'))
const FriendCellar    = lazy(() => import('./pages/FriendCellar.jsx'))
const Discover        = lazy(() => import('./pages/Discover.jsx'))
const BetaSignup      = lazy(() => import('./pages/BetaSignup.jsx'))
const Landing         = lazy(() => import('./pages/Landing.jsx'))
const Notifications        = lazy(() => import('./pages/Notifications.jsx'))
const NotificationSettings = lazy(() => import('./pages/NotificationSettings.jsx'))
const MerchantSignup  = lazy(() => import('./pages/MerchantSignup.jsx'))
const EuWaitlist      = lazy(() => import('./pages/EuWaitlist.jsx'))
const MerchantOnboard        = lazy(() => import('./pages/MerchantOnboard.jsx'))
const MerchantOnboardSuccess = lazy(() => import('./pages/MerchantOnboardSuccess.jsx'))
const Contact         = lazy(() => import('./pages/Contact.jsx'))
const Feedback        = lazy(() => import('./pages/Feedback.jsx'))
const Help            = lazy(() => import('./pages/help/Help.jsx'))
const SavedWines      = lazy(() => import('./pages/SavedWines.jsx'))
const BookmarkDetail  = lazy(() => import('./pages/BookmarkDetail.jsx'))
const Merchant        = lazy(() => import('./pages/Merchant.jsx'))
const Organization    = lazy(() => import('./pages/Organization.jsx'))
const Inventory       = lazy(() => import('./pages/Inventory.jsx'))
const InventoryItem   = lazy(() => import('./pages/InventoryItem.jsx'))
const InventoryAdd       = lazy(() => import('./pages/InventoryAdd.jsx'))
const InventoryImport    = lazy(() => import('./pages/InventoryImport.jsx'))
const InventoryIntegrate = lazy(() => import('./pages/InventoryIntegrate.jsx'))

// Merchant redesign — promoted from /merchant-preview to /merchant.
// MerchantLayout is the canonical sidebar shell. Shop and Inventory
// routes mount the real working pages (Organization, Inventory) inside
// the sidebar so the user never falls out of the merchant context.
// Legacy single-page Merchant.jsx is parked at /merchant/legacy as a
// fallback during transition; safe to remove after a couple of weeks.
const MerchantLayout      = lazy(() => import('./pages/Merchant/MerchantLayout.jsx'))
const MerchantDashboard   = lazy(() => import('./pages/Merchant/Dashboard.jsx'))
const MerchantEvents      = lazy(() => import('./pages/Merchant/EventsList.jsx'))
const MerchantEventEditor = lazy(() => import('./pages/Merchant/EventEditor.jsx'))
const MerchantEventDetail = lazy(() => import('./pages/Merchant/EventDetail.jsx'))
const MerchantBroadcastConsole = lazy(() => import('./pages/Merchant/BroadcastConsole.jsx'))
const MerchantEventScanner = lazy(() => import('./pages/Merchant/EventScanner.jsx'))
const MerchantLive        = lazy(() => import('./pages/Merchant/Live.jsx'))
const MerchantCustomers   = lazy(() => import('./pages/Merchant/Customers.jsx'))
const MerchantAnalytics   = lazy(() => import('./pages/Merchant/Analytics.jsx'))
const MerchantPromote     = lazy(() => import('./pages/Merchant/Promote.jsx'))
const MerchantPayouts     = lazy(() => import('./pages/Merchant/Payouts.jsx'))
const MerchantSubscription = lazy(() => import('./pages/Merchant/Subscription.jsx'))
const MerchantPhoneConsole = lazy(() => import('./pages/Merchant/PhoneConsole.jsx'))
const LiveBroadcast       = lazy(() => import('./pages/LiveBroadcast.jsx'))
const EventPublic         = lazy(() => import('./pages/EventPublic.jsx'))
const Shop            = lazy(() => import('./pages/Shop.jsx'))
// VIN-2026-27 (2026-05-26): /welcome theme-picker page deleted. Replaced
// by the new Login page (Phase 2 of the web/app split). The
// `vinarai_theme_pref` localStorage key the old page used to seed is
// still honored by App.jsx's theme resolver, so users who set their
// theme via /welcome BEFORE the migration keep that choice on next
// sign-in — the seed write at sign-in time mirrors it into profile.themeMode.
// Named exports need the .then(m => ({ default: ... })) shim to work with lazy().
const PrivacyPolicy   = lazy(() => import('./pages/Legal.jsx').then(m => ({ default: m.PrivacyPolicy })))
const TermsOfService  = lazy(() => import('./pages/Legal.jsx').then(m => ({ default: m.TermsOfService })))
// WineOfTheDay is mounted as a global overlay (not a route) so it's also lazy.
const WineOfTheDay    = lazy(() => import('./pages/WineOfTheDay.jsx'))

// BottomNav renders on most authenticated routes so we keep it eager —
// lazy-loading it causes a visible flash at the bottom of every page.
import BottomNav from './pages/BottomNav.jsx'
// VIN-2026-83: theme-aware "Done" toolbar above the iOS keyboard (replaces the
// un-themeable native accessory bar). Self-gates to iOS + renders only when the
// keyboard is open, so it's safe to mount globally.
import KeyboardAccessoryBar from './components/KeyboardAccessoryBar.jsx'
// VIN-2026-86: routes opened iOS Universal Links (event invite emails, shared
// wine links) into the in-app router instead of Safari. iOS-only no-op on web.
import DeepLinkHandler from './components/DeepLinkHandler.jsx'
import { ToastContainer, clearAllToasts } from './components/Toast.jsx'
import { ConfirmContainer } from './components/ConfirmDialog.jsx'
// BugReportButton early-returns null for unauthed users and renders as a
// fixed-position FAB for everyone else. Safe to lazy-load (a brief delay
// before the FAB appears post-auth is unnoticeable; unauthed pages skip
// the chunk entirely).
const BugReportButton = lazy(() => import('./components/BugReportButton.jsx'))
import { installErrorLogger } from './utils/errorLogger.js'
import { trackEvent } from './utils/analytics.js'
import { setEventRetentionMonths, logEvent } from './utils/eventLog.js'
// VIN-2026-36: attributeShopSignup moved to dynamic import in the
// first-signup branch below. See header comment.
import { useViewMode } from './utils/viewMode.js'
import { TIER_FEATURES, maxTier } from './utils/entitlements.js'
// VIN-2026-36: loadStaffStatus moved to dynamic import inside the auth
// callback. See header comment.
import { getLocalOnboardingDone, setLocalOnboardingDone } from './utils/onboardingFlag.js'
// VIN-2026-36: resolveMerchantContextFromEntitlement moved to dynamic
// import inside the auth callback. Merchant-account refactor Phase 2.2
// design context is preserved in docs/design/merchant-account-refactor.md.

// Shared loading fallback shown while a lazy-loaded route chunk is in flight.
// Matches the existing auth-loading spinner so transitions look consistent.
function RouteLoading() {
  return (
    <div className="min-h-screen bg-stone-950 flex flex-col items-center justify-center gap-4">
      <img src={vinaraiIcon} alt="Vinarai" style={{ width:64, height:64, objectFit:'contain' }} />
      <div className="w-6 h-6 border-2 border-rose-700 border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

// Clears any lingering toasts whenever the user navigates to a new route
function ToastClearer() {
  const { pathname } = useLocation()
  useEffect(() => { clearAllToasts() }, [pathname])
  return null
}

// VIN-2026-59 Phase 2 (2026-05-28): the SHOW_VINARAI_ON allowlist was
// retired. The AI FAB now appears app-wide (see HIDE_AI_FAB_ON below)
// and opens the contextual chat overlay instead of navigating to the
// full Sommelier page. "AI everywhere" per the redesign brief.

// Routes where the BottomNav should be hidden (full-screen flows)
const HIDE_NAV_ON = ['/vinarai', '/sommelier', '/login', '/signin/finish', '/onboarding', '/join-table', '/admin', '/shared/', '/beta', '/beta-signup', '/merchant', '/m', '/organization', '/shop/', '/merchant-signup', '/for-merchants', '/waitlist', '/taste/', '/tastings/', '/scan']
// Live broadcast routes (both legacy /event/:id/live in-person flow AND
// the new /event/:id/watch YouTube viewer) are focused experiences —
// the bottom nav distracts from the tasting itself.
const HIDE_NAV_CONTAINS = ['/live', '/watch']

function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase()
}

// Region inference for the multi-region data-residency plumbing.
//
// 2026-05-14: We're on us-central1 today (single Firestore project,
// option A — US-only launch with an EU waitlist). The `region` field
// on profiles is captured at signup so when EU launches as a separate
// project (vinarai-eu in eur3 multi-region) we can route users to the
// correct database via a getDb(uid) abstraction without a data
// migration. Storing the field now costs nothing; not storing it
// means a backfill on every existing profile when EU goes live.
//
// Inference uses navigator.language as a best-effort hint. The user
// can correct it via Preferences if it's wrong; admin can also bulk-
// reassign. Defaults to 'us' for unknown locales (matches the
// current Firestore project location, so no surprises).
const EU_LOCALE_PREFIXES = [
  // EEA member states (ISO 639-1 language + country codes that imply
  // EU residency). This is rough; the canonical check at signup time
  // is the user-facing geo-gate (Cloudflare country header / Vercel
  // edge geo) which we'll wire in when we add the EU waitlist gate.
  'de', 'fr', 'es', 'it', 'pt', 'nl', 'pl', 'sv', 'da', 'fi', 'no',
  'cs', 'sk', 'hu', 'ro', 'bg', 'el', 'ga', 'hr', 'sl', 'lt', 'lv',
  'et', 'mt',
  // English variants tagged with EEA countries
  'en-gb', 'en-ie', 'en-de', 'en-fr', 'en-nl', 'en-se', 'en-dk',
]
function inferRegionFromLocale(locale) {
  if (!locale) return 'us'
  const l = String(locale).toLowerCase()
  for (const prefix of EU_LOCALE_PREFIXES) {
    if (l === prefix || l.startsWith(prefix + '-') || l === prefix) return 'eu'
  }
  return 'us'
}

// VIN-2026-59 Phase 2 (2026-05-28): routes where the AI FAB should NOT
// appear — auth/onboarding flows, the full Sommelier page itself, and
// focused full-screen experiences (live tasting, broadcast viewer,
// join flows). Everywhere else the FAB is now visible so the AI is
// reachable app-wide (the user asked for "AI overlay everywhere").
// Replaces the old SHOW_VINARAI_ON allowlist, which only surfaced it
// on the cellar + events hub.
const HIDE_AI_FAB_ON = [
  '/login', '/signin/finish', '/onboarding', '/vinarai', '/sommelier',
  '/join-table', '/welcome', '/beta', '/beta-signup', '/merchant-signup',
  '/for-merchants', '/waitlist', '/shared/', '/taste/',
]

function FloatingVinarai({ user, accountType }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [viewMode] = useViewMode()
  const path = location.pathname

  if (!user) return null
  // Hide on auth/onboarding/full-screen-flow routes + live/broadcast.
  if (HIDE_AI_FAB_ON.some(p => path.startsWith(p))) return null
  if (path.includes('/live') || path.includes('/watch')) return null
  // AI doesn't apply to merchant inventory management. Hide the FAB in
  // shop view; show again in personal mode (consumer cellar flow).
  if (accountType === 'merchant' && viewMode === 'shop') return null

  return (
    <button
      data-tour="vinarai-fab"
      // VIN-2026-66 (#137): the FAB now launches the FULL Sommelier directly
      // (chat by default, with Dine Out + Analyze cards at the top) instead
      // of the stripped-down compact overlay — consolidating the two AI
      // entry points the user flagged. Wine-specific surfaces (WOTD,
      // WineDetail, cards) still call openWineChat(wine) for a quick
      // contextual "ask about THIS bottle" overlay; this general FAB is the
      // gateway to the whole experience. state.from lets Sommelier tailor
      // app-help suggestions to where you came from.
      onClick={() => navigate('/vinarai', { state: { from: path } })}
      title="Ask Vinarai"
      // VIN-018 — theme-aware FAB shadow.
      style={{ position:'fixed', bottom:104, right:20, zIndex:39, width:52, height:52, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.2s', cursor:'pointer', background:'#7B2040', boxShadow:'var(--app-fab-shadow)' }}>
      <img src={vinaraiIcon} alt="Vinarai" style={{ width:30, height:30, objectFit:'contain' }} />
    </button>
  )
}

// (Floating Vinarai button declared further up — at its definition the
// `navigate` call now stamps location.state.from so the Sommelier page
// can read the user's previous route and surface page-help suggestions.)

// ── Global Wine-of-the-Day overlay ────────────────────────────────────────────
// Listens for ?wotd=1 on any route and renders the WoTD modal on top of the
// current page. On close it strips the param from the URL so the user stays
// exactly where they were.
//
// When a user opens WOTD MANUALLY via the nav button, we stamp the same
// daily-lockout key Home.jsx uses for the auto-show — otherwise the
// auto-trigger fires on top of the manual open's data, and refreshing
// the page later re-pops the modal even though the user already saw
// today's WOTD via the button.
function GlobalWineOfTheDay({ user, userTier, tierExpiresAt, customFeatures, accountType }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const isOpen = searchParams.get('wotd') === '1'
  useEffect(() => {
    if (!isOpen) return
    const todayKey = new Date().toISOString().split('T')[0]
    try { localStorage.setItem('vinarai_wotd_shown', todayKey) } catch { /* private mode */ }
  }, [isOpen])
  if (!user || !isOpen) return null
  const handleClose = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('wotd')
    setSearchParams(next, { replace: true })
  }
  return (
    <WineOfTheDay
      user={user}
      onClose={handleClose}
      userTier={userTier}
      tierExpiresAt={tierExpiresAt}
      customFeatures={customFeatures}
      accountType={accountType}
    />
  )
}

function NavGuard({ user }) {
  const location = useLocation()
  const path = location.pathname

  // 2026-05-25: badge counts now come from the central useBadgeCounts
  // hook so BottomNav stays in sync with whatever the user has toggled
  // off in /notifications. The hook is pref-gated — if the user opts
  // out of in-app event invitations, `badges.community` returns 0
  // even when invites exist.
  const badges = useBadgeCounts(user)

  const hide =
    HIDE_NAV_ON.some(p => path.startsWith(p)) ||
    HIDE_NAV_CONTAINS.some(seg => path.includes(seg))

  // Mirror the hide-state onto a body class so the global
  // `body { padding-bottom: 90px }` (which reserves space for the
  // fixed BottomNav) can be suppressed on nav-hidden routes. Without
  // this, /vinarai + /merchant + /shop/:slug etc. would show 90px of
  // bare body background below their own footer chrome — visually
  // identical to the theme bg, but a noticeable seam to the eye and
  // (more importantly) extra page-scroll space that lets components
  // like Sommelier scroll the whole page instead of their internal
  // panes.
  useEffect(() => {
    if (typeof document === 'undefined') return
    document.body.classList.toggle('no-bottom-nav', !user || hide)
  }, [user, hide])

  if (!user || hide) return null
  return <BottomNav user={user} pendingInvites={badges.community} />
}

// ── Auth scope context (VIN-2025-10, 2026-05-26) ──────────────────────────────
// Exposes accountType + isAdmin to RequireAuth so the WebMobileOnlyNotice can
// show the right CTA (merchant dashboard / admin panel) when a signed-in user
// hits a consumer-only route on web. Pre-fix these were RequireAuth props
// that nobody actually passed, so the notice always rendered the bare
// "Get on iPhone" copy — even for merchants who could have been routed to
// /merchant/dashboard with one tap.
const AuthScopeContext = createContext({ accountType: 'personal', isAdmin: false })
export function useAuthScope() { return useContext(AuthScopeContext) }

// ── Login route: preserves and restores the intended destination ──────────────
// When a user arrives at a protected route while not logged in, App passes the
// intended path as location.state.from. After login, this component redirects
// them back there instead of always going to '/'.
function LoginRoute({ user, accessDenied }) {
  const location = useLocation()
  if (user) {
    const dest = location.state?.from || '/'
    return <Navigate to={dest} replace />
  }
  return <Login accessDenied={accessDenied} />
}

// ── Protected route: redirects to login, preserving current path ──────────────
function RequireAuth({ user, children, allowWeb = false, showMerchantCTA, showAdminCTA }) {
  const location = useLocation()
  // VIN-2025-10: read defaults from the auth-scope context so callers
  // don't have to thread merchant/admin flags through every <Route>.
  // Explicit props still win (allows opt-OUT for surfaces where the
  // CTA would be wrong, e.g. an admin tool exposed via a public link).
  const scope = useAuthScope()
  const effectiveShowMerchantCTA = showMerchantCTA ?? (scope.accountType === 'merchant')
  const effectiveShowAdminCTA    = showAdminCTA    ?? scope.isAdmin
  // Inline native detection — Capacitor injects window.Capacitor on iOS/Android.
  const isNativePlatform = typeof window !== 'undefined'
    && typeof window.Capacitor?.isNativePlatform === 'function'
    && window.Capacitor.isNativePlatform()

  if (!user) {
    // Signed-out routing per docs/strategy/web-app-split-2026-05-25.md:
    //   native → /login (Phase 2 sign-in screen with Google/Apple)
    //   web    → /      (Landing — marketing + waitlist)
    // The destination is stashed so post-sign-in handoff lands them
    // on the original deep link (when they're on native).
    if (isNativePlatform) {
      return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />
    }
    return <Navigate to="/" replace />
  }

  // Phase 4: signed-in but on a consumer route while on web → render
  // the WebMobileOnlyNotice instead of the route content. Merchant +
  // admin routes opt out via allowWeb=true. Statically imported so
  // there's no Suspense flash; the component is small enough that
  // the bundle delta is negligible.
  if (!allowWeb && !isNativePlatform) {
    return <WebMobileOnlyNotice user={user} showMerchantCTA={effectiveShowMerchantCTA} showAdminCTA={effectiveShowAdminCTA} />
  }

  return children
}

// Paths that fresh users can hit before they've completed onboarding.
// Anything else should redirect them to /onboarding so we capture their
// preferences before they start adding wines.
const ONBOARDING_EXEMPT = new Set([
  '/onboarding', '/login', '/beta', '/beta-signup',
  '/privacy', '/terms', '/contact', '/feedback',
])

function RequireOnboarded({ user, onboarded, children }) {
  const location = useLocation()
  if (!user) return children  // RequireAuth will handle unauthed redirect
  if (onboarded === false) {
    const path = location.pathname
    const isExempt = ONBOARDING_EXEMPT.has(path)
      || path.startsWith('/shared/')
      || path.startsWith('/join/')
      || /^\/event\/[^/]+\/join/.test(path)
    if (!isExempt) {
      return <Navigate to="/onboarding" replace />
    }
  }
  return children
}

function App() {
  // Boot-time housekeeping: clear the chunk-reload guard once the fresh
  // bundle has actually executed. This way each new stale-bundle situation
  // gets ONE retry, not just one per session lifetime. Runs synchronously
  // at module-eval-on-mount, before any state setup.
  useEffect(() => { clearChunkReloadFlag() }, [])

  const [user, setUser] = useState(undefined)
  const [onboarded, setOnboarded] = useState(undefined)
  const [accessDenied, setAccessDenied] = useState(false)
  // Tier from Firestore profile — passed to pages that need entitlement checks
  const [userTier, setUserTier] = useState(null)
  const [tierExpiresAt, setTierExpiresAt] = useState(null)
  const [customFeatures, setCustomFeatures] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)
  // accountType = 'personal' | 'merchant'. Flipping a user to merchant is an
  // admin action on Admin > Users; when set, App.jsx resolves their tier to
  // Sommelier and the Shop-profile section appears in Preferences so they
  // can manage their storefront fields.
  const [accountType, setAccountType] = useState('personal')
  const [merchantTier, setMerchantTier] = useState('merchant_starter')
  const [merchantProfile, setMerchantProfile] = useState(null)
  // Staff membership state — populated when a personal-account user is on
  // a shop's staff (via merchant_staff connection). Drives the staff
  // subscription view in Preferences and gates /merchant access for
  // staff. Null = either still loading or user is not staff.
  //   merchantRole: null | 'owner' | 'admin' | 'staff'
  //     - owner: accountType === 'merchant' (always)
  //     - admin: staff member with staffRole === 'admin' on their connection
  //     - staff: staff member with staffRole !== 'admin' (default)
  //     - null:  consumer not on any shop's staff
  //   staffShopInfo: when merchantRole is 'admin' or 'staff', carries the
  //     shop owner's uid, shop name, slug, and current merchantTier so
  //     pages like /merchant can load the right shop without re-querying.
  const [merchantRole, setMerchantRole]   = useState(null)
  const [staffShopInfo, setStaffShopInfo] = useState(null)
  // Ref mirror of the staff-inherited consumer tier (e.g. 'sommelier' for
  // a Pro-shop staffer). Lives in a ref because the realtime profile
  // listener (registered once at sign-in) needs to read the LATEST value
  // on every snapshot — a captured state closure would always see the
  // initial null and silently drop staff inheritance every time a
  // profile field changed. Updated alongside setStaffShopInfo at the
  // staff-detection site.
  const staffInheritedTierRef = useRef(null)
  // VIN-2025-09 (2026-05-26): cache the previous uid so the
  // unregisterPush() call on sign-out can pass it through. Without
  // this, unregisterPush() runs with no uid and its arrayRemove
  // branch silently no-ops — leaving the previous user's FCM token
  // in profiles/{prevUid}.fcmTokens forever. Account-switch on a
  // single device then sends pushes to BOTH the new and the old
  // account's tokens.
  const prevUidRef = useRef(null)
  // 2026-05-25: pendingInvites was hand-rolled state with its own
  // focus-refresh loop. Migrated into useBadgeCounts (called inside
  // NavGuard) so BottomNav, UserMenu, and future surfaces all read
  // from one source AND honor the user's notificationPrefs.inApp
  // gates. App.jsx no longer needs to know.
  // Color theme preference. 'red' (default) is the dark obsidian / burgundy
  // look the app shipped with; 'white' is the alternate warm-ivory palette.
  // Persisted to profiles/{uid}.themeMode for signed-in users so it survives
  // sign-out and syncs across devices. localStorage backs anonymous users
  // (e.g. visitors choosing on /welcome before sign-up) so their pick
  // survives a page reload AND seeds the profile.themeMode at sign-in.
  // The setThemeMode wrapper writes through to both stores; the realtime
  // profile listener picks the change up so the snap render happens
  // immediately.
  const [themeMode, setThemeModeState] = useState(() => {
    try {
      const stored = localStorage.getItem('vinarai_theme_pref')
      if (stored === 'white' || stored === 'red') return stored
    } catch { /* private browsing — fall through */ }
    return 'red'
  })
  // Stripe billing state. Kept here (vs. fetched ad-hoc in Preferences) so the
  // realtime profile onSnapshot feeds a single source of truth: Preferences
  // uses `stripeCustomerId` to decide whether the "Manage subscription" button
  // can even open a portal session (the API 404s without a customer), and
  // `subscriptionStatus` lets the UI render cancel-countdown and trial-ending
  // copy without another Firestore read.
  const [stripeCustomerId, setStripeCustomerId] = useState(null)
  const [subscriptionStatus, setSubscriptionStatus] = useState(null)
  // Billing interval ('monthly'|'annual'|null) — stamped by stripe-
  // webhook.js from the price-ID lookup so the UI can render the right
  // in-app interval-swap CTA without an extra Stripe round-trip.
  // Null when the user has no Stripe subscription on file.
  const [stripeInterval, setStripeInterval] = useState(null)
  // cancelAtPeriodEnd / cancelAt: only set when a user has canceled mid-period
  // via the billing portal. Stripe keeps the subscription active until
  // current_period_end, so we stay on the paid tier but render an "Ends in Nd"
  // countdown in Preferences instead of the standard "Access until" copy.
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false)
  const [cancelAt, setCancelAt] = useState(null)

  // Install the global error logger once per app boot. Safe to call twice
  // (the helper guards against re-install internally).
  useEffect(() => { installErrorLogger() }, [])

  // 2026-05-25: pendingInvites focus-refresh moved into useBadgeCounts
  // so NavGuard, UserMenu, and any future surface read the same source.
  // The hook also honors notificationPrefs.eventInvitations.inApp, so a
  // user who turns off in-app event invites in /notifications no longer
  // sees the community-tab dot.

  useEffect(() => {
    // Mobile redirect-flow capture. signInWithRedirect navigates the
    // browser AWAY for OAuth, then comes back to the same URL. When we
    // come back, getRedirectResult() resolves with the credential and
    // Firebase fires onAuthStateChanged. Calling consumePendingRedirect
    // here ensures the credential gets ingested even if the
    // post-redirect URL doesn't include the ?code=… params (some
    // browsers strip them). For non-mobile flows this is a cheap no-op
    // (resolves to null when there's no pending redirect).
    //
    // Errors during redirect capture are non-fatal — log them so a
    // failed OAuth handoff doesn't leave the user staring at a blank
    // page with no signal.
    import('./utils/auth.js').then(({ consumePendingRedirect }) =>
      consumePendingRedirect().catch(err => {
        console.warn('[App] sign-in redirect capture failed:', err?.code, err?.message)
      })
    )

    // Track the active profile listener so we can tear it down when the user
    // signs out or the auth state flips to a different uid. Without this, a
    // listener installed for user A keeps firing after sign-out and can
    // leak into user B's session on the next sign-in.
    let profileUnsub = null
    const detachProfile = () => {
      if (profileUnsub) { profileUnsub(); profileUnsub = null }
    }

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      // Any auth transition (sign-out OR sign-in as a different user) should
      // detach the previous session's profile listener before we do anything
      // else. This also runs on sign-out where currentUser is null.
      detachProfile()

      if (currentUser) {
        // session_start — fires once per browser session per uid. The
        // sessionStorage guard prevents the event from firing on every
        // onAuthStateChanged tick (token refresh, tab focus, etc.) — only
        // a true new-session boot lands a write. Compressor uses this for
        // DAU/MAU + per-session feature usage.
        try {
          const sessionFlag = `vinarai_session_logged_${currentUser.uid}`
          if (!sessionStorage.getItem(sessionFlag)) {
            sessionStorage.setItem(sessionFlag, '1')
            logEvent('session_start', {
              uid: currentUser.uid,
              context: {
                referrer: document?.referrer?.slice(0, 200) || null,
                pathname: window.location?.pathname || null,
              },
            })
          }
        } catch { /* sessionStorage disabled — non-fatal */ }

        // ── Allowlist check ───────────────────────────────────────────────────
        // Only users with a doc in the `allowedUsers` collection can access the app.
        // Anyone else is signed out immediately and shown an access denied message.
        //
        // Exception: skip this check on /beta so the BetaInvite page can
        // complete its registration transaction before we verify allowedUsers.
        // After registration it hard-redirects to '/', which re-triggers this
        // listener and succeeds because allowedUsers now exists.
        // Same exception applies to /merchant/onboard — the page reads
        // allowedUsers itself to validate the invite code; bouncing here
        // would lose the ?code= URL param and break the flow.
        const isBetaPath        = window.location.pathname.startsWith('/beta')
        const isJoinPath        = window.location.pathname.startsWith('/join')
        const isEventJoin       = /^\/event\/[^/]+\/join/.test(window.location.pathname)
        const isPublicShop      = window.location.pathname.startsWith('/shop/')
        const isMerchantOnboard = window.location.pathname.startsWith('/merchant/onboard')
        if (!isBetaPath && !isJoinPath && !isEventJoin && !isPublicShop && !isMerchantOnboard) {
          // VIN-2025-02 (2026-05-25): wait for the auth token BEFORE the
          // allowedUsers read. Without this, on a fresh iOS install /
          // app-update, onAuthStateChanged can fire before Firestore's
          // request interceptor has the user's ID token attached —
          // getDoc returns an empty/permission-blocked snapshot and the
          // app silently signs the user OUT, showing access-denied even
          // for users who are legitimately on the allowlist. Same race
          // shape the cellars.js fix targets, but earlier in the chain.
          // getIdToken() resolves only once the token is genuinely
          // available; cached tokens resolve sub-millisecond.
          // VIN-2025-08: 10s ceiling so a hung token refresh doesn't
          // lock the gate. Without the timeout, a stale persisted token
          // on a flaky network can stall here indefinitely. The catch
          // below tolerates a missing token; the worst case is the
          // allowedUsers getDoc that follows returns permission-denied,
          // which lands the user on the same access-denied screen the
          // race would have caused — but at least the boot completes.
          try {
            await withTimeout(currentUser.getIdToken(), 10000, 'app-getIdToken-allowlist')
          } catch (tokErr) {
            console.warn('[onAuthStateChanged] getIdToken pre-allowlist failed (continuing):', tokErr?.code || tokErr?.message)
          }
          const emailNorm = normalizeEmail(currentUser.email)
          const allowedRef = doc(db, 'allowedUsers', emailNorm)
          // VIN-2026-25 (2026-05-26): wrap the allowlist gate read in a
          // hard timeout. Without this, a stalled Firestore connection
          // (cold-start, captive portal, token race) hangs the entire
          // boot indefinitely — user-reported "first sign-in just sits
          // at loading; closing + reopening works fine." The boot needs
          // to either succeed within the timeout window or fall through
          // to a clear access-denied screen the user can retry from.
          let allowedSnap
          try {
            allowedSnap = await withTimeout(getDoc(allowedRef), 8000, 'app-allowlist-gate')
          } catch (err) {
            console.warn('[onAuthStateChanged] allowlist gate read timed out / failed:', err?.message)
            // Treat timeout / network failure the same as a missing
            // allowlist entry — sign the user out and show the access-
            // denied screen with the standard "try again" affordance.
            // Better UX than an infinite spinner.
            await signOut(auth)
            setUser(null)
            setOnboarded(null)
            setAccessDenied(true)
            return
          }
          if (!allowedSnap.exists()) {
            await signOut(auth)
            setUser(null)
            setOnboarded(null)
            setAccessDenied(true)
            return
          }
        }
        setAccessDenied(false)
        // ─────────────────────────────────────────────────────────────────────
      }

      // Normalize displayName so whitespace-only values don't leak to UI code
      // that checks truthiness (JoinEvent, EventLobby, etc.). Firebase's User
      // object has read-only properties, so we produce a plain-object wrapper
      // that still exposes the fields the app reads.
      const normalizeCurrentUser = (u) => {
        if (!u) return null
        const trimmed = (u.displayName || '').trim()
        if (trimmed === (u.displayName || '')) return u
        return new Proxy(u, {
          get(target, prop) {
            if (prop === 'displayName') return trimmed || null
            const v = target[prop]
            return typeof v === 'function' ? v.bind(target) : v
          },
        })
      }
      setUser(normalizeCurrentUser(currentUser))
      // Tag Sentry with the current user so any error reports from
      // here on are attributable. No-op when VITE_SENTRY_DSN is unset
      // OR when currentUser is null (sign-out clears the tag).
      // Lazy import to avoid pulling Sentry into the auth-state path
      // for users on builds without the DSN configured.
      import('./utils/sentry.js').then(({ setSentryUser }) =>
        setSentryUser(currentUser)
      ).catch(() => { /* sentry module load failed — non-fatal */ })
      // Native push registration. No-op on web. Idempotent on native
      // (same uid + token early-outs). Permission prompt fires the
      // first time a user signs in on a fresh install; subsequent
      // sessions reuse the granted permission silently. On sign-out
      // (currentUser=null) we detach listeners — the FCM token stays
      // in profiles/{uid}.fcmTokens because by sign-out time the auth
      // token is already invalidated and the Firestore write would
      // fail with permission-denied anyway. Stale tokens get pruned
      // server-side when a send returns invalid-token.
      // VIN-2025-09: pass the previous uid through unregisterPush so
      // its arrayRemove branch can actually run. Update prevUidRef as
      // the LAST step of each register/unregister cycle.
      import('./utils/pushNotifications.js').then(({ registerForPush, unregisterPush }) => {
        if (currentUser) {
          prevUidRef.current = currentUser.uid
          registerForPush(currentUser.uid).catch(() => { /* non-fatal */ })
        } else {
          const uidToUnregister = prevUidRef.current
          prevUidRef.current = null
          unregisterPush(uidToUnregister).catch(() => { /* non-fatal */ })
        }
      }).catch(() => { /* push module load failed — non-fatal, native-only */ })
      if (currentUser) {
        try {
          const emailNorm = normalizeEmail(currentUser.email)
          const profileRef = doc(db, 'profiles', currentUser.uid)

          // 2026-05-25: WAIT for the auth token to be ready before reading
          // profile. Without this, on a fresh iOS install (or app update)
          // onAuthStateChanged can fire BEFORE Firestore's request
          // interceptor has the user's ID token attached — getDoc then
          // returns an empty/permission-blocked snapshot and the
          // downstream profile-driven state (cellarId, themeMode, all
          // preferences) defaults to seed values, blowing away the
          // user's actual settings. getIdToken() resolves only once the
          // token is genuinely available, so awaiting it here closes the
          // race for every downstream read. Force-refresh is false so
          // cached tokens are used when valid (sub-millisecond).
          // VIN-2025-08: 10s ceiling — see allowlist branch above for
          // the full rationale. Cached tokens resolve sub-millisecond
          // so this is essentially free in the steady state.
          try {
            await withTimeout(currentUser.getIdToken(), 10000, 'app-getIdToken-profile')
          } catch (tokErr) {
            console.warn('[onAuthStateChanged] getIdToken pre-fetch failed (continuing):', tokErr?.code || tokErr?.message)
          }

          // Fetch profile and allowedUsers in parallel
          // VIN-2026-25 (2026-05-26): wrapped both reads in withTimeout
          // for the same reason as the allowlist gate above — Firestore
          // reads can hang on cold-start / token race. Promise.all
          // resolves when BOTH succeed, so wrapping each one with its
          // own timeout (rather than the combined Promise.all) means a
          // single slow read can't block the other from completing or
          // from timing out predictably.
          let [profileSnap, allowedSnap] = await Promise.all([
            withTimeout(getDoc(profileRef), 8000, 'app-profile-read'),
            emailNorm
              ? withTimeout(getDoc(doc(db, 'allowedUsers', emailNorm)), 8000, 'app-allowed-read')
              : Promise.resolve(null),
          ])

          // VIN-2026-63 (#125): fresh-install / cold-boot guard. getDoc()
          // can resolve from the EMPTY offline cache before the network is
          // ready (it does NOT throw) — so an established user's profile
          // reads as "doesn't exist," and they get wrongly re-onboarded
          // AND their saved preferences look missing. If the doc appears
          // absent, confirm against the SERVER before trusting it. Only a
          // server-CONFIRMED absent profile means a genuinely new user.
          if (!profileSnap?.exists()) {
            try {
              const serverSnap = await withTimeout(getDocFromServer(profileRef), 8000, 'app-profile-read-server')
              if (serverSnap?.exists()) profileSnap = serverSnap
            } catch (e) {
              console.warn('[App] profile server-confirm read failed:', e?.message || e)
            }
          }

          const profileData  = profileSnap?.exists()  ? profileSnap.data()  : {}
          const allowedData  = allowedSnap?.exists()  ? allowedSnap.data()  : {}

          // VIN-2026-25 (2026-05-26): apply the theme as EARLY as
          // possible. Pre-fix, the theme block ran ~300 lines later —
          // AFTER resolveMerchantContextFromEntitlement + loadStaffStatus,
          // both of which could hang and prevent the theme from
          // applying at all. User-reported "dark theme persists on
          // fresh reinstall, still not checking what's saved in the
          // database." Moving the read here means the theme is applied
          // the instant profileData is available, regardless of any
          // downstream slowness or failures in the merchant/staff
          // resolution chain.
          //
          // Theme preference resolution order:
          //   1. profile.themeMode (authoritative once written — syncs
          //      across devices via the realtime listener below)
          //   2. localStorage('vinarai_theme_pref') — set by /welcome
          //      pre-signup picker; seeded into the profile here on
          //      first sign-in so it propagates cross-device going
          //      forward.
          //   3. 'red' default
          const earlyProfileTheme = profileData?.themeMode
          if (earlyProfileTheme === 'white' || earlyProfileTheme === 'red') {
            setThemeModeState(earlyProfileTheme)
            // Mirror to localStorage so the very next cold-start render
            // (before profile fetch lands again) already shows the
            // correct theme. Pre-fix the path that resolved the profile
            // value never wrote back through to localStorage, so each
            // boot flashed the default theme until the profile read
            // completed.
            try { localStorage.setItem('vinarai_theme_pref', earlyProfileTheme) } catch { /* ignore */ }
          } else {
            // Profile has no theme yet — pull from the pre-signup localStorage
            // picker. VIN-2026-91: ONLY persist when there's a REAL stored
            // preference. Pre-fix this wrote the 'red' DEFAULT to the profile
            // whenever localStorage was empty (i.e. EVERY fresh install),
            // stamping 'red' onto the profile and clobbering the user's actual
            // saved preference on the next load — "fresh install isn't
            // referencing my theme." Now an empty localStorage just uses the
            // in-memory default and leaves profile.themeMode untouched, so the
            // realtime profile listener can apply the real choice when it lands.
            let stored = null
            try { stored = localStorage.getItem('vinarai_theme_pref') } catch { /* ignore */ }
            const hasStored = stored === 'white' || stored === 'red'
            setThemeModeState(hasStored ? stored : 'red')
            // VIN-2026-25: use the local callback arg `currentUser.uid` (the
            // `user` React state is still undefined on first sign-in).
            if (hasStored) {
              setDoc(doc(db, 'profiles', currentUser.uid), { themeMode: stored }, { merge: true })
                .catch(err => console.warn('[themeMode] seed write failed:', err.message))
            }
          }

          // Funnel: fire `signup` the first time we see an account with no
          // profile row yet (or an empty row from a seeded allowlist entry).
          // Dedupe on the `funnel_signup_at` flag so it stays at exactly once
          // per user across devices.
          if (!profileData?.funnel_signup_at) {
            trackEvent('signup', { hasAllowlist: !!allowedSnap?.exists() })
            // Mirror to the behavioral event log — the analyticsEvents
            // write above is what the admin funnel dashboard reads;
            // this mirror lands the same signal in userEvents so the
            // personalization compressor sees account-creation as a
            // single lifecycle event without joining two collections.
            logEvent('account_created', {
              uid: currentUser.uid,
              context: {
                hasAllowlist: !!allowedSnap?.exists(),
                region:       inferRegionFromLocale(navigator?.language || 'en-US'),
              },
            })
            // Mark it so next load doesn't re-fire. Uses merge so we don't
            // clobber the onboarding-driven profile write.
            //
            // 2026-05-14: also stamp `region` so we can route this user
            // to the correct Firestore project once EU launches (Option A
            // path — US-only first, EU project Q3+). Region is best-guess
            // from navigator.language; the user can correct it in
            // Preferences if needed. Defaults to 'us' for any unknown
            // locale so existing/test users get a sane default.
            try {
              const region = inferRegionFromLocale(navigator?.language || 'en-US')
              await setDoc(profileRef, {
                funnel_signup_at: new Date().toISOString(),
                region,
              }, { merge: true })
            } catch { /* non-fatal */ }

            // Shop signup attribution. AWAIT the funnel_signup_at write
            // above before firing — attributeShopSignup writes
            // referredByShop to the same profile doc and would race with
            // the merge-create otherwise (intermittent not-found from
            // updateDoc). Best-effort: the helper swallows failures and
            // clears the pending ref so we don't retry every page load.
            //
            // VIN-2026-36: dynamic-imported so the 135-line helper doesn't
            // ride along in the cold-start bundle. Only fires on FIRST
            // signup per user — by definition not hot path.
            await import('./utils/shopAttribution.js')
              .then(({ attributeShopSignup }) =>
                attributeShopSignup(currentUser.uid, currentUser.email)
              )
              .catch(() => { /* noop */ })
          }

          // 2026-05-26: stamp the user's current IANA timezone on every
          // sign-in. The Cloud Function WOTD scheduler reads this to
          // decide which users to notify each hour ("is it 5pm in their
          // local TZ right now?"). Re-stamping on every sign-in keeps
          // the value current when users travel or change device TZ.
          // Idempotent — only writes when the detected TZ differs from
          // what's already stored.
          try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
            if (tz && tz !== profileData?.timezone) {
              await setDoc(profileRef, { timezone: tz }, { merge: true })
            }
          } catch { /* non-fatal — fallback is no scheduled WOTD */ }

          // allowedUsers is the authoritative source for admin-controlled fields.
          // This ensures changes made in the Admin panel take effect on next login
          // even if the profiles doc wasn't synced at the time of the change.
          //
          // Default to 'glass' (free tier) when neither doc has a tier set so
          // userTier never stays null after the fetch completes -- otherwise
          // tier-gated components like WineOfTheDay sit forever on a "still
          // loading" state. Glass is the safe default for any account whose
          // tier metadata didn't get written (manually-added test users,
          // orphaned auth accounts, etc.); upgrades flip them to a real tier.
          //
          // VIN-2025-11 (2026-05-26): if BOTH reads came back empty (no
          // allowedUsers doc, no profile doc) this fallback silently
          // downgrades a paying user to free for the session — that's
          // hostile to debugging. Surface a Sentry breadcrumb + console
          // warn so we can see it happening in production. The VIN-02
          // getIdToken pre-fetch closes most of this gap, but a permission-
          // denied at exactly the wrong moment can still trigger it.
          const allowedEmpty = !allowedSnap?.exists()
          const profileEmpty = !profileSnap?.exists()
          if (allowedEmpty && profileEmpty) {
            console.warn('[onAuthStateChanged] BOTH allowedUsers + profile reads empty — falling back to glass tier. Possible token race or permission-denied.')
            import('./utils/sentry.js').then(({ captureMessage }) => {
              captureMessage?.('app.silent-tier-fallback', {
                level: 'warning',
                tags: { feature: 'authState', op: 'tier-fallback' },
                extra: { uid: currentUser?.uid, hasEmail: !!currentUser?.email },
              })
            }).catch(() => { /* sentry load failed — non-fatal */ })
          }
          const storedTier     = allowedData.tier           ?? profileData.tier           ?? 'glass'
          const tierExpiresAt  = allowedData.tierExpiresAt  ?? profileData.tierExpiresAt  ?? null
          const customFeatures = allowedData.customFeatures ?? profileData.customFeatures ?? null
          const isAdminVal     = allowedData.isAdmin        ?? profileData.isAdmin        ?? false

          // ── Merchant-account refactor Phase 2.4 cutover (2026-05-18) ──
          //
          // merchantEntitlements/{uid} is the ONLY source of truth for
          // merchant context now. The legacy profile.merchantProfile +
          // allowedUsers.accountType fallback was removed once the user
          // confirmed no production data needed to be migrated (only 2
          // test merchants to be deleted instead).
          //
          // resolveMerchantContextFromEntitlement returns null for:
          //   - Consumers (no entitlement doc) → accountType='personal'
          //   - Stale/missing entitlement pointers (graceful — treat as
          //     consumer; the user can re-onboard via /merchant-signup
          //     if needed)
          //
          // For the staff/admin/customer roles, the entitlement carries
          // shopOwnerUid + shopName so the staff-inheritance code below
          // doesn't have to query loadStaffStatus separately (that path
          // still works as a fallback for orgs-based staff).
          let entitlementCtx = null
          try {
            // VIN-2026-25 (2026-05-26): cap merchant-context resolution
            // at 8s. The resolver issues up to 2 chained Firestore reads
            // (entitlement doc + merchantAccount doc) and was previously
            // unbounded. On the first-sign-in token race, either read
            // could hang and block the entire boot before theme/onboarded
            // state ever settled. The catch already treats failure as
            // "consumer" — a timeout just degrades to the same path.
            //
            // VIN-2026-36: dynamic-imported. The 262-line merchantEntitlements
            // module + its lazy transitive (merchantAccounts.js) was riding
            // along on every cold start in the entry chunk; now it loads
            // exactly once per sign-in after auth completes.
            const { resolveMerchantContextFromEntitlement } = await import('./utils/merchantEntitlements.js')
            entitlementCtx = await withTimeout(
              resolveMerchantContextFromEntitlement(currentUser.uid),
              8000,
              'app-merchant-context',
            )
          } catch (err) {
            console.warn('[App] entitlement resolver failed (treating as consumer):', err?.message)
          }

          const accountTypeVal     = entitlementCtx?.accountType    ?? 'personal'
          const merchantProfileVal = entitlementCtx?.merchantProfile ?? null
          const merchantTierVal    = entitlementCtx?.merchantTier    ?? 'merchant_starter'

          if (entitlementCtx) {
            console.debug('[App] merchant-context', {
              role: entitlementCtx.role,
              merchantAccountId: entitlementCtx.merchantAccountId,
            })
          }

          // accountType is authoritative — a merchant account resolves to the
          // appropriate merchant sub-tier (merchant_starter/growth/pro).
          // Admins flip accountType on the Users tab; the underlying `tier`
          // field is preserved so reverting to a personal account keeps
          // whatever consumer tier the user had before.
          let tier = accountTypeVal === 'merchant'
            ? (TIER_FEATURES[merchantTierVal] ? merchantTierVal : 'merchant_starter')
            : storedTier
          setMerchantTier(accountTypeVal === 'merchant' ? merchantTierVal : 'merchant_starter')

          // ── Staff membership detection ──────────────────────────────────
          // For personal accounts, see if the user is on a shop's staff.
          // If yes:
          //   1. Apply staff-tier inheritance — effective tier becomes
          //      max(personalTier, inheritedFromShop). A staff member of a
          //      Pro shop gets Sommelier perks even if they're on Glass
          //      personally.
          //   2. Set merchantRole so /merchant can grant them access with
          //      role-appropriate tabs.
          // Best-effort: failures fall through with the consumer tier
          // unchanged. The consumer Preferences > Subscription view also
          // reads this, so the page-level fallback covers any race.
          // Capture the user's merchant-context role into a local so the
          // first-arrival redirect below can decide whether to route the
          // user to the merchant dashboard. Owners (accountType==='merchant')
          // AND admin/staff members of any shop should ALL land on the
          // merchant surface — pre-fix only owners did, so staff users
          // signed in and got the consumer cellar with no signal that
          // they had merchant context available.
          let merchantRoleResolved = null  // null | 'owner' | 'admin' | 'staff'
          if (accountTypeVal === 'merchant') {
            setMerchantRole('owner')
            setStaffShopInfo(null)
            staffInheritedTierRef.current = null
            merchantRoleResolved = 'owner'
          } else {
            try {
              // VIN-2026-25 (2026-05-26): bounded staff-status read.
              // loadStaffStatus issues a collection-group query + up to 2
              // additional getDoc reads for the owner profile. On cold-
              // start sign-ins those reads have been observed to hang
              // when the auth token hasn't fully propagated to the
              // Firestore request interceptor. Timing out gracefully
              // means the user gets their personal tier (no staff
              // inheritance) instead of an indefinite loading spinner;
              // a subsequent navigation re-resolves staff via the
              // existing on-demand path in Preferences.
              //
              // VIN-2026-36: dynamic-imported. The 225-line staff-status
              // helper only fires for personal accounts that might be
              // on a merchant's staff (the most common case is consumer
              // users where this returns null fast) — no reason to load
              // it on the cold-start path.
              const { loadStaffStatus } = await import('./utils/staffStatus.js')
              const s = await withTimeout(
                loadStaffStatus(currentUser.uid),
                8000,
                'app-staff-status',
              )
              if (s) {
                const role = s.staffRole === 'admin' ? 'admin' : 'staff'
                setMerchantRole(role)
                setStaffShopInfo(s)
                tier = maxTier(tier, s.inheritedTier)
                // Cache the inheritance for the realtime profile listener
                // below — without this, every profile snapshot resets
                // userTier to the personal storedTier and silently drops
                // the staff inheritance perk (a Pro-shop staffer on
                // personal Glass would flicker back to Glass every time
                // their profile doc updated).
                staffInheritedTierRef.current = s.inheritedTier
                merchantRoleResolved = role
              } else {
                setMerchantRole(null)
                setStaffShopInfo(null)
                staffInheritedTierRef.current = null
              }
            } catch (err) {
              console.warn('[App] staff status load failed:', err?.message)
              setMerchantRole(null)
              setStaffShopInfo(null)
              staffInheritedTierRef.current = null
            }
          }

          // VIN-2026-26 (2026-05-26): OR-merge the Firestore value with the
          // local fallback flag. Onboarding.finish() sets the local flag
          // BEFORE awaiting its Firestore write, so a user who completed
          // the flow but whose write didn't fully land (flaky network,
          // 15s timeout race) still bypasses the onboarding gate on next
          // launch. If the Firestore value IS true, we also mirror back
          // to localStorage as belt-and-suspenders so a localStorage
          // wipe on the next session has a chance to re-seed.
          //
          // VIN-2026-56 (2026-05-27): added a third "established user"
          // signal. The original two-source gate breaks for users who:
          //   • Reinstalled TestFlight (wipes localStorage) AND
          //   • Created their account before profile.onboardingComplete
          //     was being written (older onboarding flow, or completed
          //     through a code path that skipped that field write)
          // For those users, localOnboarded=false + firestoreOnboarded
          // =false → bounce to /onboarding despite being long-time users.
          // User-reported "loaded last build, got sent to onboarding."
          //
          // Heuristic: a profile doc with substance (tier OR
          // funnel_signup_at present) is overwhelmingly likely an
          // established account. funnel_signup_at is written on first
          // sign-in (line ~824); tier is set during the entitlement
          // resolution after profile create. A truly-new user lands
          // here BEFORE either of those fields exist, so they still
          // hit /onboarding correctly.
          //
          // We also backfill onboardingComplete=true when the heuristic
          // fires, so subsequent boots don't have to re-evaluate it.
          const firestoreOnboarded = profileData?.onboardingComplete === true
          const localOnboarded     = getLocalOnboardingDone(currentUser.uid)
          const profileHasSubstance = profileSnap.exists() && (
            !!profileData?.tier
            || !!profileData?.funnel_signup_at
            || !!profileData?.displayName
          )
          // VIN-2026-66 (recurring fresh-install re-onboard): a network-free
          // 4th signal. The three signals above ALL depend on a successful
          // Firestore profile read — but on a fresh install the read can
          // still come back empty in the brief post-OAuth window (empty
          // offline cache + auth token not yet fully propagated), even with
          // the getDocFromServer confirm above. When that happened an
          // established user was STILL bounced to /onboarding (and prefs
          // looked wiped). Firebase Auth's own creationTime is set
          // server-side at signup and lives on the user object with NO
          // network call: if the account was created more than a few minutes
          // ago, this is a returning user — never re-onboard them, even when
          // every Firestore read fails. The realtime profile listener below
          // then backfills prefs once the connection establishes.
          let accountIsEstablished = false
          try {
            const createdMs = currentUser?.metadata?.creationTime
              ? new Date(currentUser.metadata.creationTime).getTime()
              : 0
            accountIsEstablished = createdMs > 0 && (Date.now() - createdMs) > 5 * 60 * 1000
          } catch { /* metadata shape guard — non-fatal */ }
          const resolvedOnboarded = firestoreOnboarded || localOnboarded || profileHasSubstance || accountIsEstablished
          // Keep the fast local flag fresh whenever we resolve onboarded by
          // ANY signal (idempotent) so the next cold boot doesn't re-run this.
          if (resolvedOnboarded && !localOnboarded) {
            setLocalOnboardingDone(currentUser.uid, true)
          }
          // VIN-2026-56: backfill the missing field so this heuristic runs at
          // most once per user. Fire-and-forget. Only WRITE when the profile
          // read actually succeeded (doc exists) — if the read failed and we
          // resolved purely via accountIsEstablished, writing could clobber
          // an unconfirmed empty read; let the listener handle it instead.
          if (resolvedOnboarded && !firestoreOnboarded && profileSnap.exists()) {
            setDoc(profileRef, { onboardingComplete: true }, { merge: true })
              .catch(err => console.warn('[onboarding-backfill] write failed:', err?.message || err))
          }
          setOnboarded(resolvedOnboarded)
          setUserTier(tier)
          setTierExpiresAt(accountTypeVal === 'merchant' ? null : tierExpiresAt)
          setCustomFeatures(customFeatures)
          setIsAdmin(isAdminVal === true)
          setAccountType(accountTypeVal)

          // ── Tasting-rank lazy backfill ───────────────────────────────────
          // The rank field (profile.tastingRank) only gets written when a
          // user COMPLETES a tasting via recomputeAndStoreRank. Users who
          // tasted BEFORE the rank system shipped have tastings in the
          // database but no rank, so their RankChip silently hides on
          // /sharing, friend cellar headers, and "added by" pills until
          // they happen to taste again.
          //
          // This block fires a fire-and-forget backfill on sign-in when
          // the field is missing. The recompute reads the user's own
          // tastings (already permitted by rules), computes the current
          // rank from data, and writes it back to their profile. One-shot
          // per user — once written, the field stays and this branch
          // skips the recompute on future sign-ins. Failures are
          // swallowed; the chip just stays hidden until the user
          // genuinely taps Finish on a tasting.
          if (!profileData?.tastingRank) {
            import('./utils/tastingRank.js')
              .then(({ recomputeAndStoreRank }) => recomputeAndStoreRank(currentUser.uid))
              .catch(err => console.warn('[App] rank backfill failed:', err?.message))
          }

          // ── Merchant first-arrival redirect ──────────────────────────────
          // Anyone with a merchant-context role on first sign-in lands on
          // their actual workplace: the merchant dashboard on web, or the
          // phone console (/m) on mobile. Covers all three role shapes:
          //
          //   - owner: profile.accountType === 'merchant'
          //   - admin: staff doc with staffRole === 'admin' at any org
          //   - staff: staff doc with any role at any org
          //
          // Pre-fix only owners were redirected. Admin/staff users signed
          // in and got the consumer cellar with no signal that they had
          // merchant context — for someone hired to manage the shop's
          // catalog this read as "the app forgot I work here." Now all
          // three roles route to the same dashboard the UserMenu mode
          // switcher uses.
          //
          // Once-per-session via sessionStorage so they can still navigate
          // freely back to `/` during the session (and see their personal
          // cellar there — the in-Home banner nudges them back to the
          // dashboard when they want it). Personal-mode users (explicit
          // viewMode flip in UserMenu) are respected — only redirect when
          // viewMode is still the default 'shop'.
          if (merchantRoleResolved) {
            try {
              const routed = sessionStorage.getItem('vinarai_merchant_routed_v1')
              if (!routed) {
                const path = window.location.pathname
                let viewModePref = 'shop'
                try { viewModePref = localStorage.getItem('vinarai_view_mode_v1') || 'shop' } catch { /* private mode */ }
                if (path === '/' && viewModePref === 'shop') {
                  sessionStorage.setItem('vinarai_merchant_routed_v1', '1')
                  // Match the device-aware destination the UserMenu mode
                  // switcher already uses (per the merchant strategy memo):
                  // phones land at /m (restricted Phone Console), larger
                  // screens at /merchant/dashboard.
                  // 2026-05-26 fix: this onAuthStateChanged callback
                  // doesn't have React Router's `navigate` in scope
                  // (the App component is the top-level <Routes> host
                  // and doesn't call useNavigate itself). Pre-fix this
                  // line threw ReferenceError at runtime for any
                  // merchant who hit `/` mid-mount. Using
                  // window.location.replace matches the intent — a
                  // hard redirect on first sign-in is fine here, and
                  // the React tree fully re-mounts at the destination.
                  const isPhoneViewport = window.innerWidth < 768
                  window.location.replace(isPhoneViewport ? '/m' : '/merchant/dashboard')
                }
              }
            } catch { /* sessionStorage disabled — fall through silently */ }
          }
          setMerchantProfile(merchantProfileVal)
          setStripeCustomerId(profileData?.stripeCustomerId ?? null)
          setSubscriptionStatus(profileData?.subscriptionStatus ?? null)
          setStripeInterval(profileData?.stripeInterval ?? null)
          setCancelAtPeriodEnd(profileData?.cancelAtPeriodEnd === true)
          setCancelAt(profileData?.cancelAt ?? null)
          // Behavioral event-log retention preference. Reads
          // profile.eventRetentionMonths (12 / 24 / 36 / 0=forever)
          // and updates the module-scoped default in eventLog.js so
          // future logEvent() writes stamp the right expiresAt TTL.
          // Default 24 months when unset.
          setEventRetentionMonths(profileData?.eventRetentionMonths ?? 24)
          // VIN-2026-25 (2026-05-26): theme application moved earlier in
          // this callback — see the `earlyProfileTheme` block right
          // after the profile/allowedUsers Promise.all above. Keeping
          // it there means a hang in resolveMerchantContextFromEntitlement
          // or loadStaffStatus can't prevent the theme from being
          // applied. The realtime profile listener below also picks up
          // post-sign-in theme changes.

          // Cross-device preference hydration — pulls profile values
          // for cellar card layout, wine label view, and AI tag
          // suggestions into localStorage so the very first paint on
          // a new device already reflects choices made elsewhere.
          // Same write-through pattern as themeMode; writes happen from
          // each toggle's setter, this is just the read-side hydration.
          // Lazy-imported so the utils stay out of the auth bundle.
          try {
            const [cardView, labelView, aiTags, fieldTitles] = await Promise.all([
              import('./utils/cellarCardViewPref.js'),
              import('./utils/labelViewPref.js'),
              import('./utils/aiTagSuggestionsPref.js'),
              import('./utils/labelFieldTitlesPref.js'),
            ])
            cardView.syncCellarCardViewFromProfile(profileData)
            labelView.syncLabelViewFromProfile(profileData)
            aiTags.syncAiTagSuggestionsFromProfile(profileData)
            fieldTitles.syncLabelFieldTitlesFromProfile(profileData)
          } catch { /* non-fatal */ }

          // Always upsert the profile email (in case it changed) and write
          // the emailIndex entry. We deliberately DO NOT write tier /
          // tierExpiresAt / customFeatures / isAdmin / accountType here —
          // those are entitlement/security fields, locked by
          // profileStripeFields() in firestore.rules, and can only be
          // written by the Admin SDK (Stripe webhook, redeem-promo
          // endpoint, Admin panel writes run by already-admin users).
          // Session state comes from allowedUsers first anyway (storedTier
          // / tierExpiresAt / isAdminVal / accountTypeVal above), so the
          // UI stays correct even if profiles/{uid} hasn't caught up yet.
          //
          // photoURL + providerId are stashed on both docs so the admin
          // panel can render an identity card (avatar + social-icon
          // headline) without an N+1 read against the auth backend.
          const providerId = currentUser.providerData?.[0]?.providerId || null
          // displayName resolution — the user-edited name on
          // profiles/{uid}.displayName ALWAYS wins over the OAuth
          // provider value. Without this, every Google/Apple sign-in
          // would re-stamp emailIndex + profiles back to the provider's
          // default ("nick" from gmail) and wipe out the user's
          // Preferences edit ("Nicky No Lots"). If the stored value is
          // newer than what Firebase auth thinks, push it back into
          // auth.currentUser too so the rest of the session sees the
          // canonical name.
          const storedDisplayName = profileData?.displayName || ''
          const authDisplayName   = currentUser.displayName || ''
          const canonicalDisplayName = storedDisplayName || authDisplayName
          if (storedDisplayName && storedDisplayName !== authDisplayName) {
            // Best-effort — failures here aren't fatal; the displayName
            // resolution above already prefers the stored value for our
            // own writes.
            updateProfile(currentUser, { displayName: storedDisplayName }).catch(() => {})
          }
          await Promise.all([
            setDoc(profileRef, {
              email: currentUser.email,
              emailNorm,
              displayName: canonicalDisplayName,
              photoURL:    currentUser.photoURL    || null,
              providerId,
            }, { merge: true }),
            emailNorm
              ? setDoc(doc(db, 'emailIndex', emailNorm), {
                  uid: currentUser.uid,
                  displayName: canonicalDisplayName,
                  photoURL:    currentUser.photoURL    || null,
                  providerId,
                  updatedAt: serverTimestamp(),
                }, { merge: true })
              : Promise.resolve(),
          ])

          // Ensure the user has a personal cellar doc + cellarId field on
          // their profile. Idempotent: existing users keep their current
          // cellarId; legacy users (pre-migration) get one created lazily
          // on first sign-in. See src/utils/cellars.js + the household
          // merge-cellars spec for the full schema rationale.
          ensureCellarForProfile(currentUser.uid, profileData).catch(err => {
            if (typeof console !== 'undefined') console.warn('[cellars] ensure failed:', err)
          })

          // VIN-2026-36: dynamic-imported. claimEventInvitesForUser is a
          // fire-and-forget post-auth claim that runs ONCE per sign-in
          // — perfect candidate for deferral from the cold-start bundle.
          import('./utils/eventInvites.js')
            .then(({ claimEventInvitesForUser }) => claimEventInvitesForUser(db, currentUser))
            .catch(() => {})

          // ── Realtime profile subscription ────────────────────────────────
          // After the initial sync from allowedUsers → profiles, attach a
          // listener to profiles/{uid} so admin changes (tier, accountType,
          // customFeatures, merchantProfile, isAdmin) flow to the live
          // session without a reload. Admin writes mirror to both the
          // allowedUsers doc AND profiles/{uid} via emailIndex lookup, so
          // listening on profiles catches every admin-driven update.
          //
          // We re-apply the same accountType-over-tier precedence as the
          // initial load: merchant accounts resolve to Sommelier even if the
          // stored `tier` string says otherwise.
          profileUnsub = onSnapshot(profileRef, (snap) => {
            if (!snap.exists()) return
            const p = snap.data() || {}
            // Phase 2.4 cutover: merchantEntitlements/{uid} is the only
            // source for accountType / merchantTier / merchantProfile.
            // This listener no longer re-derives them from profile
            // snapshots — even if profile.accountType somehow gets set
            // (admin SDK only, defense in depth), it's ignored here.
            // The legacy merchantContextSourceRef is gone; the listener
            // always preserves the entitlement-resolved merchant state.
            //
            // Non-merchant fields (tier, customFeatures, displayName,
            // theme, etc) continue to flow through this listener as
            // before — they aren't part of this refactor.
            const nextAccountType = accountType  // keep current — entitlement owns this
            // Only update tier-related fields when profile actually carries
            // them. Without this guard, a profile doc that lacks `tier`
            // (e.g. brand-new beta users whose tier is set on allowedUsers
            // but never mirrored to profiles -- profile.tier writes from
            // the client are blocked by profileStripeFields() in the rules)
            // would clobber the userTier that was just resolved from
            // allowedUsers, leaving the session stuck on null. Admin writes
            // DO mirror tier into profile via the redeem-promo / Stripe
            // webhook Admin SDK paths, so the listener still picks up live
            // admin changes -- it just stops second-guessing the initial
            // allowedUsers resolution when profile is silent.
            // p.merchantTier check kept for back-compat with stale
            // profile docs that still have the field (admins might
            // have legacy docs from pre-cutover that we haven't
            // proactively cleared). New writes never set it.
            if (p.tier !== undefined || p.merchantTier !== undefined) {
              if (nextAccountType === 'merchant') {
                // Entitlement owns merchantTier; this listener no longer
                // reacts to p.merchantTier. Keep userTier consistent
                // with the entitlement-resolved merchantTier state.
                setUserTier(merchantTier || 'merchant_starter')
              } else {
                // Re-apply the staff-inheritance ceiling that the initial
                // load resolved. Without this, any profile doc update
                // (admin edit, customFeatures change, even funnel_signup_at
                // being stamped) drops a Pro-shop staffer from Sommelier
                // back to their personal Glass tier — visible regression
                // that flickers UI mid-session.
                const personalTier = p.tier ?? null
                const inherited = staffInheritedTierRef.current
                setUserTier(inherited ? maxTier(personalTier, inherited) : personalTier)
              }
            } else if (nextAccountType === 'merchant') {
              // Account is merchant — force tier even if profile never
              // had a tier field of its own.
              setUserTier(merchantTier || 'merchant_starter')
            }
            if (p.tierExpiresAt !== undefined) {
              setTierExpiresAt(nextAccountType === 'merchant' ? null : p.tierExpiresAt)
            }
            if (p.customFeatures !== undefined) setCustomFeatures(p.customFeatures)
            setIsAdmin(p.isAdmin === true)
            // Phase 2.4 cutover: accountType + merchantProfile are owned
            // by the entitlement now. The realtime listener never
            // re-derives them — preserves the entitlement state across
            // profile snapshots (theme toggle, funnel_signup_at stamp, etc).
            setStripeCustomerId(p.stripeCustomerId ?? null)
            setSubscriptionStatus(p.subscriptionStatus ?? null)
            setStripeInterval(p.stripeInterval ?? null)
            setCancelAtPeriodEnd(p.cancelAtPeriodEnd === true)
            setCancelAt(p.cancelAt ?? null)
            // Event-log retention preference (see initial load above).
            // Re-applied on every profile snapshot so a user changing
            // the toggle in Preferences takes effect immediately.
            setEventRetentionMonths(p.eventRetentionMonths ?? 24)
            // onboardingComplete can also flip (admin reset) — reflect it so
            // gating re-engages without a reload.
            //
            // VIN-2026-26 (2026-05-26): OR-merge with the local fallback
            // flag here too. If an admin resets profile.onboardingComplete
            // back to false (intentional, to force a user through onboarding
            // again), the local flag would override the reset — so we
            // CLEAR the local flag when Firestore explicitly transitions
            // false. The mirror-on-true keeps the local flag fresh when
            // the user completes elsewhere (web → app sync).
            if (typeof p.onboardingComplete === 'boolean') {
              if (p.onboardingComplete) {
                setLocalOnboardingDone(currentUser.uid, true)
                setOnboarded(true)
              } else {
                // Admin reset — clear the local fallback so re-onboarding
                // actually re-engages. Re-check the local flag in case it
                // was just set by an in-flight Onboarding.finish() (rare
                // race, but safer to honor recent-local-write over an
                // older Firestore false).
                const stillLocal = getLocalOnboardingDone(currentUser.uid)
                if (!stillLocal) setOnboarded(false)
              }
            }
            // Theme preference flips immediately when the user toggles in
            // Preferences (and on any other device for the same account).
            if (p.themeMode === 'white' || p.themeMode === 'red') {
              setThemeModeState(p.themeMode)
            }
            // Cross-device live-sync for the three localStorage-backed
            // prefs that now write through to the profile doc. Each
            // sync helper is a no-op when the field is absent, so
            // checking is cheap and we only need a guard for the
            // common-case skip (no field changed → don't even import).
            if (p.cellarCardView || p.labelView || typeof p.aiTagSuggestionsEnabled === 'boolean' || typeof p.labelFieldTitles === 'boolean') {
              Promise.all([
                import('./utils/cellarCardViewPref.js'),
                import('./utils/labelViewPref.js'),
                import('./utils/aiTagSuggestionsPref.js'),
                import('./utils/labelFieldTitlesPref.js'),
              ]).then(([cardView, labelView, aiTags, fieldTitles]) => {
                cardView.syncCellarCardViewFromProfile(p)
                labelView.syncLabelViewFromProfile(p)
                aiTags.syncAiTagSuggestionsFromProfile(p)
                fieldTitles.syncLabelFieldTitlesFromProfile(p)
              }).catch(() => { /* non-fatal */ })
            }
          }, (err) => {
            // Permission errors here would indicate a rules regression —
            // worth a console log but not a user-visible error since the
            // one-shot read above already populated state.
            console.warn('[profile onSnapshot] listener error:', err?.message || err)
          })
        } catch {
          setOnboarded(true)
        }
      } else {
        setOnboarded(null)
        // Drop any cached cellarId so the next signed-in session re-resolves
        // it against the new user's profile.
        clearCellarCache()
      }
    })
    return () => {
      detachProfile()
      unsubscribe()
    }
  }, [])


  // Theme write-through: optimistically updates local state for instant
  // re-render, then persists to profiles/{uid}.themeMode so the choice
  // survives sign-out and syncs across devices. profile.themeMode is NOT
  // in profileStripeFields(), so the user can self-write it -- the rule
  // allows any field outside that locked list.
  const setThemeMode = async (mode) => {
    if (mode !== 'red' && mode !== 'white') return
    setThemeModeState(mode)
    // Always mirror to localStorage so anonymous users keep their pick
    // across reloads, AND we have a value to seed the profile with on
    // first sign-in (see auth listener below).
    try { localStorage.setItem('vinarai_theme_pref', mode) } catch { /* ignore */ }
    if (!user?.uid) return
    try {
      await setDoc(doc(db, 'profiles', user.uid), { themeMode: mode }, { merge: true })
    } catch (err) {
      console.warn('[setThemeMode] persist failed:', err)
    }
  }

  const [viewMode] = useViewMode()
  const effectiveAccountType = accountType === 'merchant' && viewMode === 'personal' ? 'personal' : accountType

  // VIN-2026-99 (iOS review P2): memoize the auth-scope context value so it
  // isn't a fresh object on every App render (which re-rendered EVERY consumer
  // every time). Declared BEFORE the loading early-return below so the hook is
  // never called conditionally.
  const authScope = useMemo(() => ({ accountType, isAdmin }), [accountType, isAdmin])

  if (user === undefined || (user && onboarded === undefined)) {
    return (
      <div className="min-h-screen bg-stone-950 flex flex-col items-center justify-center gap-4">
        <img src={vinaraiIcon} alt="Vinarai" style={{ width:64, height:64, objectFit:'contain' }} />
        <div className="w-6 h-6 border-2 border-rose-700 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <ThemeProvider themeMode={themeMode} setThemeMode={setThemeMode}>
      <AuthScopeContext.Provider value={authScope}>
      <RequireOnboarded user={user} onboarded={onboarded}>
      <Suspense fallback={<RouteLoading />}>
      <Routes>
        {/* Login — restores destination after sign-in via location.state.from */}
        <Route path="/login" element={<LoginRoute user={user} accessDenied={accessDenied} />} />
        {/* VIN-2026-15 (2026-05-26): magic-link sign-in return URL.
            Email tap → /signin/finish?apiKey=...&oobCode=... → page
            auto-completes via signInWithEmailLink. PUBLIC route — the
            user isn't signed in yet at this point. */}
        <Route path="/signin/finish" element={<SignInFinish />} />

        {/* Public routes */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/shared/:token" element={<SharedWineView />} />
        <Route path="/shop/:slug" element={<Shop />} />
        <Route path="/join-table" element={<JoinTable user={user} />} />
        <Route path="/event/:eventId/join" element={<JoinEvent user={user} />} />
        <Route path="/event/:eventId/summary" element={<EventSummary user={user} />} />
        <Route path="/event/:eventId/live" element={<LiveTasting user={user} />} />
        {/* Consumer-side YouTube live broadcast viewer. Mounted under
            the existing /event/:id namespace so deep links from
            email/social land naturally. Public — no auth required;
            anyone with the link can watch. */}
        <Route path="/event/:eventId/watch" element={<LiveBroadcast />} />
        {/* Public event detail (new-schema events under
            organizations/{orgId}/events). Browse + RSVP entry point;
            served at /e/:eventId. RSVP requires sign-in (the page
            redirects to /login if anonymous). */}
        <Route path="/e/:eventId" element={<EventPublic user={user} />} />
        <Route path="/sommelier" element={<Navigate to="/vinarai" replace />} />

        {/* Beta invite — public, handles its own auth flow */}
        <Route path="/beta" element={<BetaInvite />} />
        <Route path="/beta-signup" element={<BetaSignup />} />
        {/* Merchant funnel — public landing page that shop owners reach
            via QR code on a one-pager. Tier comparison + signup form +
            triages into Admin > Shop Apps. */}
        <Route path="/merchant-signup" element={<MerchantSignup />} />
        <Route path="/waitlist/eu" element={<EuWaitlist />} />
        <Route path="/for-merchants"   element={<Navigate to="/merchant-signup" replace />} />

        {/* Merchant onboarding — destination of the approval-email link.
            Public on purpose: arrives unauthenticated with ?code=… and
            handles the full sign-in → tier-confirm → Stripe Checkout
            flow inline. Success page polls profile.subscriptionStatus
            for the webhook write before redirecting to /merchant/shop. */}
        <Route path="/merchant/onboard"         element={<MerchantOnboard />} />
        <Route path="/merchant/onboard/success" element={<MerchantOnboardSuccess />} />

        {/* Cellar sharing invite — public, handles sign-in internally */}
        <Route path="/join/:code" element={<JoinCellar user={user} />} />

        {/* Protected routes — all use RequireAuth so the destination is preserved */}
        {/* Root routing matrix (post web/app split — see
            docs/strategy/web-app-split-2026-05-25.md):
              signed-in (any platform) → Home
              unsigned web             → Landing (marketing page + waitlist)
              unsigned native (iOS app) → /login (Phase 2 sign-up/sign-in
                                          with Google/Apple brand logos +
                                          theme toggle; replaces the old
                                          /welcome theme-picker page,
                                          deleted in VIN-2026-27)
            Deep links (e.g. /wine/:id) still redirect to /login via
            RequireAuth so the post-signin handoff preserves the destination. */}
        <Route path="/" element={
          user
            ? <RequireAuth user={user}><Home user={user} onboarded={onboarded} userTier={userTier} tierExpiresAt={tierExpiresAt} customFeatures={customFeatures} accountType={effectiveAccountType} merchantRole={merchantRole} /></RequireAuth>
            : (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.())
              ? <Navigate to="/login" replace />
              : <Landing />
        } />
        <Route path="/add" element={<RequireAuth user={user}><AddWine user={user} userTier={userTier} tierExpiresAt={tierExpiresAt} customFeatures={customFeatures} accountType={effectiveAccountType} /></RequireAuth>} />
        {/* VIN-2026-11 (2026-05-26): "Check a Wine" — scan a label in a
            wine shop / restaurant and get a Yes/No/Maybe taste-match
            verdict without committing to add the wine to the cellar.
            Reuses the existing scanLabel + quickEnrichWine pipeline. */}
        <Route path="/check-wine" element={<RequireAuth user={user}><CheckWine user={user} /></RequireAuth>} />
        {/* VIN-2026-63 Ship D: the unified camera-first scan — the center
            [Scan] tab lands here. Adapts its result to whether the bottle
            is already in your cellar (Uncork) or new (Add / Taste / Bookmark). */}
        <Route path="/scan" element={<RequireAuth user={user}><ScanFlow user={user} /></RequireAuth>} />
        <Route path="/wine/:id" element={<RequireAuth user={user}><WineDetail user={user} userTier={userTier} tierExpiresAt={tierExpiresAt} customFeatures={customFeatures} accountType={effectiveAccountType} /></RequireAuth>} />
        <Route path="/wine/:id/edit" element={<RequireAuth user={user}><EditWine user={user} userTier={userTier} tierExpiresAt={tierExpiresAt} customFeatures={customFeatures} accountType={effectiveAccountType} /></RequireAuth>} />
        <Route path="/taste/:wineId" element={<RequireAuth user={user}><TastingFlow user={user} /></RequireAuth>} />
        <Route path="/tastings" element={<RequireAuth user={user}><TastingHistory user={user} /></RequireAuth>} />
        <Route path="/tastings/:tastingId" element={<RequireAuth user={user}><TastingHistory user={user} /></RequireAuth>} />
        <Route path="/vinarai" element={<RequireAuth user={user}><VinaraiPage user={user} userTier={userTier} tierExpiresAt={tierExpiresAt} customFeatures={customFeatures} /></RequireAuth>} />
        <Route path="/bulk" element={<RequireAuth user={user}><BulkUpload user={user} userTier={userTier} tierExpiresAt={tierExpiresAt} customFeatures={customFeatures} /></RequireAuth>} />
        <Route path="/preferences" element={<RequireAuth user={user}><Preferences user={user} userTier={userTier} tierExpiresAt={tierExpiresAt} customFeatures={customFeatures} isAdmin={isAdmin} accountType={effectiveAccountType} merchantProfile={merchantProfile} merchantRole={merchantRole} stripeCustomerId={stripeCustomerId} subscriptionStatus={subscriptionStatus} stripeInterval={stripeInterval} cancelAtPeriodEnd={cancelAtPeriodEnd} cancelAt={cancelAt} onMerchantProfileChange={setMerchantProfile} onUserRefresh={() => setUser(auth.currentUser ? { ...auth.currentUser } : null)} /></RequireAuth>} />
        {/* Events pages — tabbed sub-navigation across 5 sections */}
        <Route path="/events" element={<RequireAuth user={user}><PublicEvents user={user} userTier={userTier} tierExpiresAt={tierExpiresAt} customFeatures={customFeatures} /></RequireAuth>} />
        <Route path="/events/hosting" element={<RequireAuth user={user}><EventsHosting user={user} userTier={userTier} tierExpiresAt={tierExpiresAt} customFeatures={customFeatures} /></RequireAuth>} />
        <Route path="/events/attending" element={<RequireAuth user={user}><EventsAttending user={user} userTier={userTier} tierExpiresAt={tierExpiresAt} customFeatures={customFeatures} /></RequireAuth>} />
        <Route path="/events/attended" element={<RequireAuth user={user}><Attended user={user} userTier={userTier} tierExpiresAt={tierExpiresAt} customFeatures={customFeatures} /></RequireAuth>} />

        {/* Ticket wallet (Phase G2.1). Lists every ticket the user holds
            across every event/host; tap into a single ticket for the
            full QR display. Free RSVPs and paid Stripe-checkout tickets
            both surface here. */}
        <Route path="/tickets" element={<RequireAuth user={user}><Tickets /></RequireAuth>} />
        {/* Ticket claim landing — public (no auth required to view the
            invite card; auth is required only to actually claim).
            URL format: /tickets/claim?orgId=&eventId=&id=&token=.
            MUST come BEFORE /tickets/:ticketId so React Router doesn't
            treat 'claim' as a ticketId. */}
        <Route path="/tickets/claim" element={<TicketClaim />} />
        <Route path="/tickets/:ticketId" element={<RequireAuth user={user}><TicketDetail /></RequireAuth>} />
        {/* Notifications inbox — auto-clears delivered iOS notifications
            and the badge on mount. Firestore-backed history populates
            once Phase B's server-side send infra writes each send to
            notifications/{uid}/items (currently just the badge-clear UX). */}
        <Route path="/notifications" element={<RequireAuth user={user}><Notifications user={user} /></RequireAuth>} />
        <Route path="/notifications/settings" element={<RequireAuth user={user}><NotificationSettings user={user} /></RequireAuth>} />
        <Route path="/events/invitations" element={<RequireAuth user={user}><Invitations user={user} /></RequireAuth>} />
        {/* Legacy /events route redirects to public events landing */}
        <Route path="/invitations" element={<Navigate to="/events/invitations" replace />} />
        <Route path="/event/create" element={<RequireAuth user={user}><CreateEvent user={user} userTier={userTier} tierExpiresAt={tierExpiresAt} customFeatures={customFeatures} /></RequireAuth>} />
        {/* VIN-041 — alias for shared/old links that use /create-event.
            Was silently redirecting to / (Cellar) before, leaving the
            user confused. Redirect explicitly to the canonical create
            route. */}
        <Route path="/create-event" element={<Navigate to="/event/create" replace />} />
        <Route path="/event/:eventId/lobby" element={<RequireAuth user={user}><EventLobby user={user} /></RequireAuth>} />
        <Route path="/sharing" element={<RequireAuth user={user}><Sharing user={user} userTier={userTier} tierExpiresAt={tierExpiresAt} customFeatures={customFeatures} /></RequireAuth>} />
        <Route path="/saved" element={<RequireAuth user={user}><SavedWines user={user} /></RequireAuth>} />
        <Route path="/bookmark/:bookmarkId" element={<RequireAuth user={user}><BookmarkDetail user={user} /></RequireAuth>} />
        {/* ── Merchant ── canonical route tree at /merchant/* ────────────
            All merchant-facing surfaces nested under MerchantLayout so
            the sidebar persists across Dashboard, Shop, Events, Live,
            Inventory (with all its sub-flows), Customers, Analytics,
            and Promote. Nesting the inventory sub-pages here means
            users never lose the sidebar when adding/editing wines or
            running a CSV import. */}
        <Route path="/merchant" element={<RequireAuth user={user} allowWeb={true}><MerchantLayout user={user} /></RequireAuth>}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<MerchantDashboard user={user} />} />
          <Route path="shop"      element={<Organization user={user} />} />
          {/* Events flow: list / create / detail / edit. The editor handles
              both new and edit paths via the :eventId param. */}
          <Route path="events"                    element={<MerchantEvents user={user} />} />
          <Route path="events/new"                element={<MerchantEventEditor user={user} />} />
          <Route path="events/:eventId"           element={<MerchantEventDetail user={user} userTier={userTier} />} />
          <Route path="events/:eventId/edit"      element={<MerchantEventEditor user={user} />} />
          <Route path="events/:eventId/broadcast" element={<MerchantBroadcastConsole user={user} userTier={userTier} />} />
          <Route path="events/:eventId/scan"      element={<MerchantEventScanner user={user} />} />
          <Route path="live"      element={<MerchantLive user={user} userTier={userTier} />} />
          <Route path="customers" element={<MerchantCustomers user={user} />} />
          <Route path="analytics" element={<MerchantAnalytics user={user} />} />
          <Route path="promote"   element={<MerchantPromote user={user} />} />
          {/* Stripe Connect Express onboarding (Phase G1). Owner-only;
              the page itself surfaces the "owner-only" gate. */}
          <Route path="payouts"   element={<MerchantPayouts user={user} />} />
          {/* Subscription tab — self-serve plan management. Added
              2026-05-18 (M1 from workflow review). Required for paid
              merchants to manage their Vinarai plan without contacting
              support. */}
          <Route path="subscription" element={<MerchantSubscription user={user} />} />
          {/* Inventory + sub-flows. All under the sidebar so add /
              import / integrate / edit never drop the chrome. */}
          <Route path="inventory"            element={<Inventory user={user} />} />
          <Route path="inventory/add"        element={<InventoryAdd />} />
          <Route path="inventory/import"     element={<InventoryImport user={user} />} />
          <Route path="inventory/integrate"  element={<InventoryIntegrate user={user} />} />
          <Route path="inventory/new"        element={<InventoryItem user={user} />} />
          <Route path="inventory/:itemId"    element={<InventoryItem user={user} />} />
        </Route>

        {/* Legacy single-page /merchant tool — parked at /merchant/legacy
            during the transition. Safe to remove once the new flow has
            been live for a couple of weeks. */}
        <Route path="/merchant/legacy" element={<RequireAuth user={user} allowWeb={true}><Merchant user={user} accountType={accountType} isAdmin={isAdmin} merchantProfile={merchantProfile} userTier={userTier} merchantRole={merchantRole} staffShopInfo={staffShopInfo} stripeInterval={stripeInterval} /></RequireAuth>} />

        {/* Fresh-signup landing — /organization stays standalone (no
            sidebar) so users without an org yet can run the inline
            create flow before they have any merchant chrome to nest
            under. After creation they're routed to /merchant/dashboard. */}
        <Route path="/organization" element={<RequireAuth user={user} allowWeb={true}><Organization user={user} /></RequireAuth>} />

        {/* Backward-compat redirects — /merchant-preview and standalone
            /inventory deep links bounce to the canonical /merchant tree.
            Wildcard suffix preserves nested paths. */}
        <Route path="/merchant-preview"   element={<Navigate to="/merchant/dashboard"  replace />} />
        <Route path="/merchant-preview/*" element={<Navigate to="/merchant/dashboard"  replace />} />
        <Route path="/inventory"          element={<Navigate to="/merchant/inventory"  replace />} />
        <Route path="/inventory/*"        element={<Navigate to="/merchant/inventory"  replace />} />

        {/* ── Phone Merchant Console — /m ── */}
        <Route path="/m" element={<RequireAuth user={user} allowWeb={true}><MerchantPhoneConsole user={user} /></RequireAuth>} />
        <Route path="/help" element={<RequireAuth user={user} allowWeb={true}><Help user={user} /></RequireAuth>} />
        <Route path="/help/:section" element={<RequireAuth user={user} allowWeb={true}><Help user={user} /></RequireAuth>} />
        {/* VIN-2026-22 (2026-05-26): /discover now redirects into the
            Community tab's Public Cellars sub-tab so all "find cellars"
            entry points (UserMenu, Notifications empty-state, Following
            section "find more") land on the unified surface instead of
            a standalone page. The Discover component is kept lazy-loaded
            and unused for now; safe to delete in a follow-up cleanup. */}
        <Route path="/discover" element={<Navigate to="/events?tab=cellars" replace />} />
        <Route path="/cellar/:uid" element={<RequireAuth user={user}><FriendCellar user={user} userTier={userTier} tierExpiresAt={tierExpiresAt} customFeatures={customFeatures} /></RequireAuth>} />
        <Route path="/friends" element={<RequireAuth user={user}><FriendCellar user={user} userTier={userTier} tierExpiresAt={tierExpiresAt} customFeatures={customFeatures} /></RequireAuth>} />
        {/* /contact is public — linked from Landing/MerchantSignup
            footers, so the page needs to render for unsigned web
            visitors. Contact.jsx handles undefined user gracefully
            (PageHeader skips the avatar slot, the form doesn't gate
            on auth). Native users still reach it via the in-app menu. */}
        <Route path="/contact" element={<Contact user={user} />} />
        <Route path="/feedback" element={<RequireAuth user={user} allowWeb={true}><Feedback user={user} /></RequireAuth>} />
        <Route path="/onboarding" element={<RequireAuth user={user} allowWeb={true}><Onboarding user={user} onComplete={() => setOnboarded(true)} /></RequireAuth>} />
        <Route path="/admin" element={
          <RequireAuth user={user} allowWeb={true}>
            {isAdmin
              ? <Admin user={user} />
              : <Navigate to="/" replace />}
          </RequireAuth>
        } />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      </Suspense>
      </RequireOnboarded>
      <ScrollToTop />
      <ToastClearer />
      <NavGuard user={user} />
      <KeyboardAccessoryBar />
      <DeepLinkHandler />
      <FloatingVinarai user={user} accountType={accountType} />
      {/* VIN-2026-59 Phase 2: global contextual AI chat overlay. Mounted
          once; driven by the aiChat.js store so any surface can pop it
          (over WOTD, over the cellar, etc.). z-index 1000 sits above
          the WOTD overlay + bottom nav. */}
      <WineChatOverlay user={user} />
      {/* WOTD is lazy and mounted as an overlay; a null fallback keeps the
          background page visible while the chunk loads. */}
      <Suspense fallback={null}>
        <GlobalWineOfTheDay user={user} userTier={userTier} tierExpiresAt={tierExpiresAt} customFeatures={customFeatures} accountType={accountType} />
      </Suspense>
      <ToastContainer />
      <ConfirmContainer />
      <Suspense fallback={null}>
        <BugReportButton
          user={user}
          userTier={userTier}
          accountType={accountType}
        />
      </Suspense>
      </AuthScopeContext.Provider>
    </ThemeProvider>
  )
}

export default App
